import { t, tx } from '../../i18n';
import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../../components/brand/Logo';

// หน้าสาธารณะ (ไม่ต้องล็อกอิน ไม่ใช้ Layout) — เส้นทางที่แนะนำ: /privacy
// ข้อความต้องตรงกับพฤติกรรมของโค้ดจริง; จุดที่โรงเรียนต้องกรอก/ตัดสินใจ แสดงเป็นกรอบเหลือง "โรงเรียนต้องกรอก"
// ปรับ LAST_UPDATED ทุกครั้งที่แก้ข้อความหรือเปลี่ยนวิธีที่ระบบใช้ข้อมูล
const LAST_UPDATED = '23 ตุลาคม 2569';
const VERSION = '1.0';

function Fill({ children }) {
  return (
    <span className="inline-block rounded border border-yellow-400 bg-yellow-50 px-2 py-1 text-sm text-yellow-900">
      <strong>{t('โรงเรียนต้องกรอก:')}</strong> {children}
    </span>
  );
}

function Section({ id, title, children }) {
  return (
    <section aria-labelledby={id} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-ink-200 sm:p-6">
      <h2 id={id} className="mb-3 text-lg font-semibold text-brand-900">{title}</h2>
      <div className="space-y-3 text-base leading-relaxed text-ink-800">{children}</div>
    </section>
  );
}

const ROLES = [
  ['ผู้ดูแลระบบ', 'ข้อมูลทั้งหมดในระบบ จัดการนักเรียน ครู ห้อง วิชา งาน และนำเข้า/ส่งออกข้อมูล'],
  ['ครู', 'รายชื่อ สถานะการส่งงาน และคะแนนของนักเรียนในหน้าตรวจงาน/รายงาน (ระบบยังไม่จำกัดการดูตามห้องที่สอน) แต่แก้ไขหรือตรวจงานได้เฉพาะวิชาที่ตนเองสอน และสร้างลิงก์ผู้ปกครองได้เฉพาะนักเรียนในห้องที่เป็นครูประจำชั้นหรือชั้นที่ตนสอน'],
  ['นักเรียน', 'เฉพาะข้อมูลการส่งงานของตนเอง'],
  ['ผู้ปกครอง (ผ่านลิงก์)', 'ชื่อจริงนักเรียน (ไม่รวมนามสกุล) ห้อง ชื่อวิชา ชื่องาน กำหนดส่ง และสถานะส่งงาน ไม่เห็นคะแนน หมายเหตุของครู หรือข้อมูลนักเรียนคนอื่น'],
  ['ผู้ให้บริการ AI ภายนอก', 'เฉพาะเมื่อโรงเรียนเปิดใช้ผู้ช่วย AI และครูกดถาม (ดูหัวข้อ 3)'],
];

export default function PrivacyNotice() {
  React.useEffect(() => {
    const prev = document.title;
    document.title = t('ความเป็นส่วนตัวและการใช้ข้อมูล — KruScan');
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="min-h-screen bg-page text-ink">
      <header className="bg-title text-title-fg">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3">
          <Logo size={32} tone="white" />
          <Link to="/login" className="inline-flex min-h-[44px] items-center rounded px-3 text-sm underline">{t('เข้าสู่ระบบ')}</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-6 pb-[calc(2rem+env(safe-area-inset-bottom))]">
        <div>
          <h1 className="text-2xl font-bold text-brand-900 sm:text-3xl">{t('ความเป็นส่วนตัวและการใช้ข้อมูล')}</h1>
          <p className="mt-2 text-base text-ink-700">{t('KruScan (ครูสแกน) ใช้บันทึกว่านักเรียนส่งงานแล้วหรือยัง หน้านี้บอกว่าระบบเก็บข้อมูลอะไร ใครเห็นอะไร และคุณมีสิทธิ์อะไรบ้าง')}</p>
          <p className="mt-1 text-xs text-ink-500">{t('ปรับปรุงล่าสุด {LAST_UPDATED} · เวอร์ชัน {VERSION}', { LAST_UPDATED: t(LAST_UPDATED), VERSION: VERSION })}</p>
        </div>

        <Section id="p1" title={t("1. ข้อมูลที่เก็บ")}>
          <p><strong>{t('นักเรียน:')}</strong>{t(' ชื่อ-นามสกุล รหัสนักเรียน เลขที่ในห้อง ห้องเรียน อีเมล (ถ้ามี) และสถานะ')}</p>
          <p><strong>{t('ครู:')}</strong>{t(' ชื่อ รหัสครู อีเมล (ถ้ามี) และรหัสผ่านที่เข้ารหัสแบบทางเดียว (bcrypt) ระบบไม่เก็บรหัสผ่านเป็นข้อความธรรมดา')}</p>
          <p><strong>{t('การส่งงาน:')}</strong>{t(' งานที่มอบหมาย กำหนดส่ง สถานะ (ยังไม่ส่ง รอตรวจ ผ่าน ไม่ผ่าน) วันที่ส่ง วันที่ตรวจ ครูผู้ตรวจ คะแนน (เฉพาะงานที่ให้คะแนน) และหมายเหตุของครู')}</p>
          <p>{tx('<b>รหัส QR:</b> สร้างคู่กับนักเรียนและวิชา เพื่อสแกนส่งงาน', { b: (c) => <strong>{c}</strong> })}</p>
          <p className="text-sm text-ink-600">{t('ระบบไม่เก็บเลขบัตรประชาชน ที่อยู่ เบอร์โทร รูปถ่าย หรือข้อมูลอ่อนไหว (เช่น สุขภาพ ศาสนา) และไม่เก็บไฟล์งานของนักเรียน')}</p>
          <p>{t('ข้อมูลนำเข้าโดยผู้ดูแลระบบหรือครู ไม่ได้เก็บจากนักเรียนโดยตรง')}</p>
          <p>
            {tx('<b>วัตถุประสงค์:</b> ติดตามการส่งงานและแจ้งผู้เกี่ยวข้อง', { b: (c) => <strong>{c}</strong> })}{' '}<Fill>{t('ยืนยันฐานทางกฎหมายที่โรงเรียนใช้ (เช่น ภารกิจเพื่อประโยชน์สาธารณะ/การจัดการศึกษา หรือความยินยอมจากผู้ปกครอง)')}</Fill>
          </p>
        </Section>

        <Section id="p2" title={t("2. ใครเห็นอะไร")}>
          <ul className="space-y-3">
            {ROLES.map(([who, what]) => (
              <li key={who} className="rounded border border-ink-200 p-3">
                <p className="font-semibold text-brand-900">{t(who)}</p>
                <p className="mt-1 text-sm text-ink-700">{t(what)}</p>
              </li>
            ))}
          </ul>
          <p className="text-sm text-ink-600">{t('การเข้าสู่ระบบ: ใช้ชื่อผู้ใช้และรหัสผ่าน ล็อกอินผิดซ้ำเกิน 5 ครั้งใน 15 นาทีจะถูกพักชั่วคราว และเซสชันหมดอายุภายใน 12 ชั่วโมง')}</p>
        </Section>

        <Section id="p3" title={t("3. การส่งข้อมูลให้บริการภายนอก (ผู้ช่วย AI)")}>
          <p>{tx('ผู้ช่วย AI <b>ปิดอยู่ จนกว่าผู้ดูแลระบบจะตั้งค่าคีย์ผู้ให้บริการ</b> ถ้าไม่ได้ตั้งค่า ไม่มีข้อมูลใดออกจากระบบของโรงเรียนไปยัง AI', { b: (c) => <strong>{c}</strong> })}</p>
          <p>{t('เมื่อเปิดใช้และครูกดถามหรือให้ AI ช่วยสรุป ระบบจะส่งข้อมูลต่อไปนี้ไปยังผู้ให้บริการ AI ภายนอก (Anthropic, Google Gemini หรือ OpenAI ตามที่โรงเรียนเลือก):')}</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>{tx('รหัส เลขที่ <b>ชื่อ-นามสกุลจริง</b> และห้องของนักเรียนในขอบเขตที่ถาม (ระบบยังไม่ปกปิดชื่อก่อนส่ง)', { b: (c) => <strong>{c}</strong> })}</li>
            <li>{t('จำนวนงานแต่ละสถานะ และเลขงานที่ค้าง')}</li>
            <li>{t('ชื่องาน ชื่อวิชา ชื่อครู กำหนดส่ง')}</li>
            <li>{t('คำถามที่ครูพิมพ์ และข้อความตัวอย่างจากไฟล์นำเข้าเมื่อใช้ตัวช่วยนำเข้า')}</li>
          </ul>
          <p className="text-sm text-ink-600">{t('ระบบไม่ส่งรหัสผ่าน อีเมล คะแนนรายบุคคล หรือหมายเหตุของครูให้ AI ผู้ให้บริการอาจประมวลผลนอกประเทศไทย')}</p>
          <p><Fill>{t('ชื่อผู้ให้บริการ AI ที่ใช้จริง, สถานะข้อตกลงประมวลผลข้อมูล (DPA), และประเทศที่ประมวลผล')}</Fill></p>
        </Section>

        <Section id="p4" title={t("4. ลิงก์ผู้ปกครอง")}>
          <ul className="list-disc space-y-1 pl-5">
            <li>{tx('ครูสร้างลิงก์ต่อนักเรียนหนึ่งคน ผู้ปกครองเปิดดูได้โดยไม่ต้องล็อกอิน <b>ใครมีลิงก์ก็เปิดได้</b> จึงควรส่งให้ผู้ปกครองโดยตรงและไม่แชร์ต่อ', { b: (c) => <strong>{c}</strong> })}</li>
            <li>{t('ลิงก์หมดอายุอัตโนมัติ (ค่าเริ่มต้น 120 วัน ปรับได้ที่เซิร์ฟเวอร์) หรือเมื่อนักเรียนถูกตั้งเป็นไม่ใช้งาน')}</li>
            <li>{t('หน้าที่เห็นแสดงเฉพาะ: ชื่อจริง (ไม่มีนามสกุล) ห้อง วิชา ชื่องาน กำหนดส่ง และสถานะส่งงาน')}</li>
            <li>{t('หน้าถูกตั้งไม่ให้เก็บแคชและไม่ให้เสิร์ชเอนจินจัดทำดัชนี')}</li>
            <li>{t('ระบบยังไม่มีปุ่มยกเลิกลิงก์รายบุคคลก่อนหมดอายุ ')}<Fill>{t('ขั้นตอนเมื่อลิงก์หลุด (เช่น ติดต่อผู้ดูแลระบบเพื่อเปลี่ยนรหัสลับของลิงก์)')}</Fill></li>
          </ul>
          <p><Fill>{t('วิธีขอความยินยอมหรือแจ้งผู้ปกครองก่อนแจกลิงก์')}</Fill></p>
        </Section>

        <Section id="p5" title={t("5. ระยะเวลาเก็บ")}>
          <p>{tx('ระบบ<b>ไม่ลบข้อมูลอัตโนมัติ</b> ข้อมูลอยู่จนกว่าผู้ดูแลระบบจะลบหรือตั้งสถานะนักเรียนเป็นไม่ใช้งาน การตั้งเป็นไม่ใช้งานไม่ได้ลบข้อมูลออกจากฐานข้อมูล', { b: (c) => <strong>{c}</strong> })}</p>
          <p><Fill>{t('ระยะเวลาเก็บ (เช่น กี่ปีหลังนักเรียนจบ/ย้ายออก) และใครเป็นผู้รับผิดชอบการลบ')}</Fill></p>
        </Section>

        <Section id="p6" title={t("6. ความปลอดภัย")}>
          <ul className="list-disc space-y-1 pl-5">
            <li>{t('รหัสผ่านเข้ารหัสด้วย bcrypt ไม่เก็บเป็นข้อความธรรมดา')}</li>
            <li>{t('เซสชันใช้โทเคน (JWT) หมดอายุใน 12 ชั่วโมง')}</li>
            <li>{t('จำกัดการล็อกอินผิดซ้ำ และจำกัดการใช้ผู้ช่วย AI ต่อผู้ใช้')}</li>
            <li>{t('ส่งข้อมูลผ่าน HTTPS เมื่อโรงเรียนติดตั้งใช้งานบนโดเมนที่เปิด HTTPS ')}<Fill>{t('ยืนยันว่าเปิด HTTPS แล้ว')}</Fill></li>
          </ul>
          <p className="text-sm text-ink-600">{t('หากพบข้อมูลรั่วไหลหรือสงสัยว่ามีผู้ไม่มีสิทธิ์เข้าถึง แจ้งตามช่องทางในหัวข้อ 7 ทันที')}</p>
        </Section>

        <Section id="p7" title={t("7. สิทธิของเจ้าของข้อมูลและช่องทางติดต่อ")}>
          <p>{t('ตามกฎหมายคุ้มครองข้อมูลส่วนบุคคล (PDPA) นักเรียนและผู้ปกครองมีสิทธิ์ขอเข้าถึงและขอสำเนา ขอแก้ไขให้ถูกต้อง ขอลบหรือระงับการใช้ คัดค้านการประมวลผล และถอนความยินยอม (ถ้าใช้ความยินยอมเป็นฐาน) โดยไม่เสียค่าใช้จ่าย')}</p>
          <dl className="space-y-2 rounded border border-yellow-400 bg-yellow-50 p-3 text-sm text-yellow-900">
            <div><dt className="font-semibold">{t('โรงเรียนต้องกรอก: ชื่อโรงเรียน (ผู้ควบคุมข้อมูล)')}</dt><dd>{t('[ชื่อโรงเรียน/ที่อยู่]')}</dd></div>
            <div><dt className="font-semibold">{t('โรงเรียนต้องกรอก: เจ้าหน้าที่คุ้มครองข้อมูล (DPO) หรือผู้รับผิดชอบ')}</dt><dd>{t('[ชื่อ-ตำแหน่ง]')}</dd></div>
            <div><dt className="font-semibold">{t('โรงเรียนต้องกรอก: อีเมล / โทรศัพท์ / ช่องทางยื่นคำขอ')}</dt><dd>{t('[อีเมล] · [เบอร์โทร]')}</dd></div>
            <div><dt className="font-semibold">{t('โรงเรียนต้องกรอก: ระยะเวลาตอบคำขอ')}</dt><dd>{t('[เช่น ภายใน 30 วัน]')}</dd></div>
          </dl>
          <p className="text-sm text-ink-600">{t('หากไม่พอใจการดำเนินการ ร้องเรียนได้ที่สำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (สคส.)')}</p>
        </Section>

        <Section id="p8" title={t("8. การปรับปรุงหน้านี้")}>
          <p>{t('ปรับปรุงล่าสุด {LAST_UPDATED} (เวอร์ชัน {VERSION}) เมื่อวิธีใช้ข้อมูลเปลี่ยน จะแก้หน้านี้และเปลี่ยนวันที่', { LAST_UPDATED: t(LAST_UPDATED), VERSION: VERSION })}</p>
        </Section>
      </main>
    </div>
  );
}
